// Content model for the optional right context rail.
//
// The rail never fetches: pages build this model from data the center canvas already
// owns and register it upward through the shell provider. That makes "no duplicate
// requests between center and rail" true by construction, and it keeps the rail an
// inspector over the current context rather than an independent feed.
//
// Functional-honesty rules are enforced structurally at render time:
// - an item with neither href nor onSelect is a static info row, never a button;
// - a disabled item must carry a visible disabledReason;
// - a section in the 'empty' state renders emptyText, never an error;
// - retry renders only for the 'error' state (a real failed request).

import type { ReactNode } from 'react';

export type RailSectionKind = 'context' | 'related' | 'status' | 'activity' | 'actions';

export type RailSectionState = 'loading' | 'empty' | 'ready' | 'error' | 'forbidden';

export type RailItem = {
  id: string;
  label: string;
  /** Right-aligned value (a count, a state word, a date). */
  value?: string;
  /** Secondary line under the label. */
  hint?: string;
  href?: string;
  onSelect?: () => void;
  icon?: ReactNode;
  badge?: number;
  disabled?: boolean;
  disabledReason?: string;
};

export type RailSection = {
  id: string;
  kind: RailSectionKind;
  title: string;
  state: RailSectionState;
  items: RailItem[];
  emptyText?: string;
  errorText?: string;
  retry?: () => void;
};

export type ContextRailContent = {
  /**
   * Identity of the object the rail is describing (e.g. "institution-overview:<id>").
   * The provider drops registered content whose key belongs to a previous route so a
   * navigation can never show a stale inspector.
   */
  key: string;
  title: string;
  sections: RailSection[];
};

export function isActionableItem(item: RailItem): boolean {
  return !item.disabled && (typeof item.onSelect === 'function' || typeof item.href === 'string');
}
